import React, { useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';

const AnimationContainer = styled.div`
  width: 100%;
  height: 600px;
  position: relative;
  overflow: hidden;
  background: linear-gradient(to bottom, #0a1517, #121e23);
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  margin: 2rem 0;
  
  @media (max-width: 768px) {
    height: 900px;
  }
`;

const ParkingArea = styled.div`
  width: 70%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  overflow: hidden;
  perspective: 1000px;
  
  @media (max-width: 768px) {
    width: 100%;
    height: 50%;
  }
`;

const ParkingFloor = styled.div`
  width: 100%;
  height: 70%;
  position: absolute;
  top: 15%;
  transform: rotateX(60deg);
  transform-origin: center bottom;
  background-image: linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px),
                    linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
  background-size: 50px 50px;
  background-color: #0d171a;
  border: 2px solid rgba(0, 227, 204, 0.3);
  box-shadow: 0 0 30px rgba(0, 0, 0, 0.5);
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  grid-template-rows: repeat(3, 1fr);
  gap: 10px;
  padding: 20px;
`;

const Wall = styled.div`
  width: 100%;
  height: 100px;
  position: absolute;
  top: 0;
  background-color: #1a2a30;
  border-bottom: 2px solid rgba(0, 227, 204, 0.3);
`;

const CCTV = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
`;

const CCTVMount = styled.div`
  width: 10px;
  height: 40px;
  background-color: #333;
  margin: 0 auto;
`;

const CCTVBody = styled.div`
  width: 40px;
  height: 25px;
  background-color: #222;
  border-radius: 5px;
  position: relative;
  margin-bottom: 5px;
  border: 1px solid #444;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.5);
  animation: rotate 5s infinite ease-in-out;
  
  @keyframes rotate {
    0% { transform: rotateY(-20deg); }
    50% { transform: rotateY(20deg); }
    100% { transform: rotateY(-20deg); }
  }
`;

const CCTVLens = styled.div`
  width: 15px;
  height: 15px;
  background: radial-gradient(circle, #000, #333);
  border-radius: 50%;
  position: absolute;
  top: 5px;
  left: 12px;
  border: 1px solid #555;
`;

const CCTVLight = styled.div`
  width: 6px;
  height: 6px;
  background-color: #FFCF3E;
  border-radius: 50%;
  position: absolute;
  top: 5px;
  right: 5px;
  box-shadow: 0 0 5px #FFCF3E;
  animation: blink 2s infinite;
  
  @keyframes blink {
    0%, 20%, 40%, 60%, 100% { opacity: 0.8; }
    10%, 30%, 50% { opacity: 1; }
  }
`;

// Custom curved spotlight with point at camera - height increased by 40%
const SpotlightContainer = styled.div`
  position: absolute;
  top: 25px;
  left: 50%;
  transform: translateX(-50%) rotate(${props => props.$rotation}deg);
  transform-origin: top center;
  width: 600px;
  height: 560px; /* Increased from 400px to 560px (40% more) */
  z-index: 5;
  pointer-events: none;
  transition: transform 0.5s linear; /* Smoother transition */
`;

const SpotlightCurve = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(ellipse at top, rgba(255, 207, 62, 0.2) 0%, rgba(255, 207, 62, 0) 70%);
  clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
`;

const ParkingSlot = styled.div`
  background-color: ${props => props.$status === 'occupied' ? 'rgba(255, 92, 92, 0.1)' : props.$status === 'selected' ? 'rgba(255, 207, 62, 0.1)' : 'rgba(0, 227, 204, 0.05)'};
  border: 3px solid ${props => props.$status === 'occupied' ? '#ff3a3a' : props.$status === 'selected' ? '#ffcc00' : '#00ff8a'};
  border-radius: 5px;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: bold;
  color: ${props => props.$status === 'occupied' ? '#FF5C5C' : props.$status === 'selected' ? '#FFCF3E' : '#00E3CC'};
  transition: all 0.3s ease;
  box-shadow: 0 0 10px ${props => props.$status === 'occupied' ? 'rgba(255, 58, 58, 0.3)' : props.$status === 'selected' ? 'rgba(255, 204, 0, 0.3)' : 'rgba(0, 255, 138, 0.3)'};
`;

const Car = styled.div`
  width: 65%;
  height: 60%;
  position: absolute;
  background-image: url(${props => props.$carImage});
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  z-index: 2;
  box-shadow: 0 3px 6px rgba(0,0,0,0.3);
`;

const AppInterface = styled.div`
  width: 30%;
  height: 100%;
  position: absolute;
  right: 0;
  top: 0;
  background-color: rgba(10, 15, 20, 0.8);
  border-left: 2px solid rgba(0, 227, 204, 0.3);
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 768px) {
    width: 100%;
    height: 50%;
    top: 50%;
    border-left: none;
    border-top: 2px solid rgba(0, 227, 204, 0.3);
  }
`;

const Phone = styled.div`
  width: 90%;
  max-width: 280px;
  height: 500px;
  background-color: #111;
  border-radius: 30px;
  overflow: hidden;
  border: 8px solid #222;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  
  @media (max-width: 768px) {
    width: 70%;
    height: 350px;
  }
  
  @media (max-width: 576px) {
    width: 90%;
    height: 300px;
  }
`;

const PhoneScreen = styled.div`
  width: 100%;
  height: 100%;
  background-color: #0d1518;
  padding: 15px;
  display: flex;
  flex-direction: column;
`;

const PhoneHeader = styled.div`
  background-color: #00E3CC;
  padding: 10px;
  text-align: center;
  border-radius: 8px;
  margin-bottom: 15px;
  
  h2 {
    color: #0a1a1f;
    font-size: 16px;
    margin: 0;
  }
`;

const PhoneMap = styled.div`
  flex: 1;
  border: 1px solid rgba(0, 227, 204, 0.3);
  border-radius: 8px;
  margin-bottom: 15px;
  overflow: hidden;
  position: relative;
  background-color: rgba(255, 255, 255, 0.05);
`;

const PhoneSlots = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  grid-template-rows: repeat(3, 1fr);
  gap: 5px;
  padding: 10px;
`;

const PhoneSlot = styled.div`
  background-color: ${props => props.$status === 'occupied' ? 'rgba(255, 92, 92, 0.1)' : props.$status === 'selected' ? 'rgba(255, 207, 62, 0.1)' : 'rgba(0, 227, 204, 0.1)'};
  border: 2px solid ${props => props.$status === 'occupied' ? '#FF5C5C' : props.$status === 'selected' ? '#FFCF3E' : '#00E3CC'};
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 12px;
  color: ${props => props.$status === 'occupied' ? '#FF5C5C' : props.$status === 'selected' ? '#FFCF3E' : '#00E3CC'};
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    width: 70%;
    height: 4px;
    background-color: ${props => props.$status === 'occupied' ? '#FF5C5C' : 'transparent'};
    bottom: 3px;
    border-radius: 2px;
  }
`;

const PhoneButton = styled.div`
  width: 100%;
  padding: 12px;
  text-align: center;
  background-color: #00E3CC;
  color: #0a1a1f;
  border-radius: 8px;
  font-weight: bold;
  cursor: pointer;
  margin-bottom: 15px;
  transition: all 0.3s ease;
  
  &:hover {
    opacity: 0.9;
  }
`;

const PhoneTabs = styled.div`
  display: flex;
  justify-content: space-between;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 15px;
`;

const PhoneTab = styled.div`
  color: ${props => props.$active ? '#00E3CC' : 'rgba(255, 255, 255, 0.5)'};
  font-size: 12px;
  padding: 5px 10px;
`;

const Notification = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%) translateY(${props => props.$show ? '0' : '100px'});
  background-color: #00E3CC;
  color: #0a1a1f;
  padding: 15px 30px;
  border-radius: 8px;
  font-weight: bold;
  opacity: ${props => props.$show ? '1' : '0'};
  transition: all 0.5s ease;
  z-index: 50;
  text-align: center;
`;

const SmartParkingAnimation = () => {
  const parkingFloorRef = useRef(null);
  const phoneSlotsRef = useRef(null);
  const notificationRef = useRef(null);
  const [slots, setSlots] = React.useState(Array(15).fill('available'));
  const [showNotification, setShowNotification] = React.useState(false);
  const [spotlightRotation, setSpotlightRotation] = React.useState(0);
  const [isAnimating, setIsAnimating] = React.useState(false);
  const slotPositionsRef = useRef([]);
  
  // Function to create stylish data particle
  const createDataParticle = useCallback((slotId) => {
    if (!document.querySelector('.cctv-body') || !phoneSlotsRef.current) return;
    
    const fromElement = document.querySelector('.cctv-body');
    const toElement = phoneSlotsRef.current.querySelectorAll('.phone-slot')[slotId - 1];
    
    if (!fromElement || !toElement) return;
    
    const fromRect = fromElement.getBoundingClientRect();
    const toRect = toElement.getBoundingClientRect();
    
    // Create container for the particle
    const particleContainer = document.createElement('div');
    particleContainer.className = 'data-particle-container';
    particleContainer.style.position = 'fixed';
    particleContainer.style.top = '0';
    particleContainer.style.left = '0';
    particleContainer.style.width = '100%';
    particleContainer.style.height = '100%';
    particleContainer.style.pointerEvents = 'none';
    particleContainer.style.zIndex = '100';
    document.body.appendChild(particleContainer);
    
    // Create main particle
    const particle = document.createElement('div');
    particle.className = 'data-particle';
    particle.style.position = 'absolute';
    particle.style.width = '16px'; // Bigger
    particle.style.height = '16px'; // Bigger
    particle.style.backgroundColor = '#FFCF3E';
    particle.style.borderRadius = '50%';
    particle.style.boxShadow = '0 0 15px #FFCF3E'; // More glow
    particle.style.zIndex = '20';
    particle.style.pointerEvents = 'none';
    
    // Create API-like data packet effect
    const dataPacket = document.createElement('div');
    dataPacket.className = 'data-packet';
    dataPacket.style.position = 'absolute';
    dataPacket.style.width = '40px';
    dataPacket.style.height = '20px';
    dataPacket.style.backgroundColor = 'rgba(255, 207, 62, 0.2)';
    dataPacket.style.border = '1px solid rgba(255, 207, 62, 0.5)';
    dataPacket.style.borderRadius = '4px';
    dataPacket.style.display = 'flex';
    dataPacket.style.justifyContent = 'center';
    dataPacket.style.alignItems = 'center';
    dataPacket.style.fontSize = '10px';
    dataPacket.style.color = '#FFCF3E';
    dataPacket.style.fontWeight = 'bold';
    dataPacket.style.transform = 'translate(-50%, -50%)';
    dataPacket.textContent = `#${slotId}`;
    
    // Add elements to container
    particleContainer.appendChild(particle);
    particleContainer.appendChild(dataPacket);
    
    // Initial position
    particle.style.left = `${fromRect.left + fromRect.width / 2}px`;
    particle.style.top = `${fromRect.top + fromRect.height / 2}px`;
    particle.style.opacity = '0';
    
    dataPacket.style.left = `${fromRect.left + fromRect.width / 2}px`;
    dataPacket.style.top = `${fromRect.top + fromRect.height / 2}px`;
    dataPacket.style.opacity = '0';
    
    // Animation
    setTimeout(() => {
      // Fade in
      particle.style.transition = 'all 0.3s ease-in-out';
      particle.style.opacity = '1';
      
      dataPacket.style.transition = 'all 0.3s ease-in-out';
      dataPacket.style.opacity = '1';
      
      setTimeout(() => {
        // Move to target
        particle.style.transition = 'all 1.5s cubic-bezier(0.165, 0.84, 0.44, 1)';
        particle.style.left = `${toRect.left + toRect.width / 2}px`;
        particle.style.top = `${toRect.top + toRect.height / 2}px`;
        
        dataPacket.style.transition = 'all 1.5s cubic-bezier(0.165, 0.84, 0.44, 1)';
        dataPacket.style.left = `${toRect.left + toRect.width / 2}px`;
        dataPacket.style.top = `${toRect.top + toRect.height / 2}px`;
        
        // Create trail effect
        for (let i = 0; i < 5; i++) {
          setTimeout(() => {
            const trail = document.createElement('div');
            trail.className = 'data-trail';
            trail.style.position = 'absolute';
            trail.style.width = '8px';
            trail.style.height = '8px';
            trail.style.backgroundColor = 'rgba(255, 207, 62, 0.5)';
            trail.style.borderRadius = '50%';
            trail.style.zIndex = '19';
            trail.style.pointerEvents = 'none';
            trail.style.opacity = '0.7';
            
            // Position trail at a point along the path
            const progress = i / 5;
            const trailX = fromRect.left + fromRect.width / 2 + (toRect.left + toRect.width / 2 - fromRect.left - fromRect.width / 2) * progress;
            const trailY = fromRect.top + fromRect.height / 2 + (toRect.top + toRect.height / 2 - fromRect.top - fromRect.height / 2) * progress;
            
            trail.style.left = `${trailX}px`;
            trail.style.top = `${trailY}px`;
            
            particleContainer.appendChild(trail);
            
            // Fade out trail
            setTimeout(() => {
              trail.style.transition = 'all 0.5s ease-out';
              trail.style.opacity = '0';
              trail.style.transform = 'scale(1.5)';
              
              setTimeout(() => {
                if (particleContainer.contains(trail)) {
                  particleContainer.removeChild(trail);
                }
              }, 500);
            }, 300);
          }, i * 200);
        }
        
        // Arrival effect
        setTimeout(() => {
          // Pulse effect at destination
          const pulse = document.createElement('div');
          pulse.className = 'data-pulse';
          pulse.style.position = 'absolute';
          pulse.style.width = '30px';
          pulse.style.height = '30px';
          pulse.style.backgroundColor = 'rgba(255, 207, 62, 0.3)';
          pulse.style.borderRadius = '50%';
          pulse.style.zIndex = '18';
          pulse.style.pointerEvents = 'none';
          pulse.style.left = `${toRect.left + toRect.width / 2}px`;
          pulse.style.top = `${toRect.top + toRect.height / 2}px`;
          pulse.style.transform = 'translate(-50%, -50%) scale(0)';
          pulse.style.opacity = '1';
          
          particleContainer.appendChild(pulse);
          
          setTimeout(() => {
            pulse.style.transition = 'all 0.5s ease-out';
            pulse.style.transform = 'translate(-50%, -50%) scale(2)';
            pulse.style.opacity = '0';
            
            // Highlight the target slot
            toElement.style.transition = 'all 0.3s ease';
            toElement.style.boxShadow = '0 0 15px #FFCF3E';
            toElement.style.transform = 'scale(1.05)';
            
            setTimeout(() => {
              // Remove highlight
              toElement.style.boxShadow = '';
              toElement.style.transform = 'scale(1)';
              
              // Fade out and remove all elements
              particle.style.transition = 'all 0.3s ease-out';
              particle.style.opacity = '0';
              particle.style.transform = 'scale(0.5)';
              
              dataPacket.style.transition = 'all 0.3s ease-out';
              dataPacket.style.opacity = '0';
              dataPacket.style.transform = 'translate(-50%, -50%) scale(0.5)';
              
              setTimeout(() => {
                if (document.body.contains(particleContainer)) {
                  document.body.removeChild(particleContainer);
                }
              }, 300);
            }, 500);
          }, 10);
        }, 1500);
      }, 300);
    }, 10);
  }, []);
  
  // Store slot positions when component mounts
  useEffect(() => {
    const calculateSlotPositions = () => {
      if (parkingFloorRef.current) {
        const floorRect = parkingFloorRef.current.getBoundingClientRect();
        const slotElements = parkingFloorRef.current.querySelectorAll('.parking-slot');
        
        const positions = Array.from(slotElements).map(slot => {
          const slotRect = slot.getBoundingClientRect();
          return {
            left: slotRect.left - floorRect.left + (slotRect.width / 2),
            top: slotRect.top - floorRect.top + (slotRect.height / 2),
            width: slotRect.width,
            height: slotRect.height
          };
        });
        
        slotPositionsRef.current = positions;
      }
    };
    
    // Calculate positions after a short delay to ensure DOM is fully rendered
    setTimeout(calculateSlotPositions, 500);
    
    // Recalculate on window resize
    window.addEventListener('resize', calculateSlotPositions);
    
    return () => {
      window.removeEventListener('resize', calculateSlotPositions);
    };
  }, []);
  
  // Function to create moving car with fixed positioning
  const createMovingCar = useCallback((targetSlotId) => {
    if (!parkingFloorRef.current || isAnimating || slotPositionsRef.current.length === 0) return;
    
    // Set isAnimating to true to prevent multiple cars
    setIsAnimating(true);
    
    const floorRect = parkingFloorRef.current.getBoundingClientRect();
    
    // Get the target slot position from our stored positions
    const targetPosition = slotPositionsRef.current[targetSlotId - 1];
    if (!targetPosition) {
      setIsAnimating(false);
      return;
    }
    
    // Create moving car - increased size by 20%
    const car = document.createElement('div');
    car.className = 'moving-car';
    car.style.backgroundImage = `url(${targetSlotId % 2 === 0 ? '/car2.png' : '/car.png'})`;
    car.style.backgroundSize = 'contain';
    car.style.backgroundRepeat = 'no-repeat';
    car.style.backgroundPosition = 'center';
    car.style.width = targetSlotId % 2 === 0 ? '108px' : '96px'; // Increased by 20%
    car.style.height = targetSlotId % 2 === 0 ? '72px' : '60px'; // Increased by 20%
    car.style.position = 'absolute';
    car.style.zIndex = '15';
    car.style.transition = 'all 1s ease';
    car.style.boxShadow = '0 3px 10px rgba(0,0,0,0.5)';
    
    // Position car at entry point
    const entryVariation = (Math.random() * 50) - 25; // -25 to 25 pixels variation
    car.style.left = `${floorRect.width}px`;
    car.style.top = `${(floorRect.height / 2) + entryVariation}px`;
    car.style.transform = 'scale(0.8)';
    car.style.opacity = '0';
    parkingFloorRef.current.appendChild(car);
    
    // Calculate angle for spotlight
    const cameraX = window.innerWidth / 2;
    const cameraY = 25; // Approximate camera Y position
    const slotCenterX = targetPosition.left + floorRect.left;
    const slotCenterY = targetPosition.top + floorRect.top;
    
    // Calculate angle between camera and slot
    const angleRad = Math.atan2(slotCenterY - cameraY, slotCenterX - cameraX);
    const angleDeg = angleRad * (180 / Math.PI);
    
    // Update spotlight rotation
    setSpotlightRotation(angleDeg);
    
    // Animate car appearance
    setTimeout(() => {
      car.style.opacity = '1';
      car.style.transform = 'scale(1)';
      
      // Animate car directly to target slot
      setTimeout(() => {
        // Calculate the position for the car to be centered in the slot
        const carWidth = targetSlotId % 2 === 0 ? 108 : 96;
        const carHeight = targetSlotId % 2 === 0 ? 72 : 60;
        
        // Position the car exactly in the center of the slot
        const carLeft = targetPosition.left - (carWidth / 2);
        const carTop = targetPosition.top - (carHeight / 2);
        
        car.style.left = `${carLeft}px`;
        car.style.top = `${carTop}px`;
        
        // Create spotlight effect when car reaches slot
        setTimeout(() => {
          // Create spotlight
          const spotlight = document.createElement('div');
          spotlight.className = 'spotlight';
          spotlight.style.position = 'absolute';
          spotlight.style.width = '80px';
          spotlight.style.height = '80px';
          spotlight.style.borderRadius = '50%';
          spotlight.style.background = 'radial-gradient(circle, rgba(255, 92, 92, 0.3) 0%, rgba(255, 92, 92, 0) 70%)';
          spotlight.style.zIndex = '8';
          spotlight.style.pointerEvents = 'none';
          spotlight.style.transform = 'scale(0)';
          spotlight.style.opacity = '0';
          spotlight.style.transition = 'all 0.3s ease';
          spotlight.style.left = `${targetPosition.left - 40}px`;
          spotlight.style.top = `${targetPosition.top - 40}px`;
          parkingFloorRef.current.appendChild(spotlight);
          
          // Show spotlight
          setTimeout(() => {
            spotlight.style.transform = 'scale(1)';
            spotlight.style.opacity = '1';
            
            // Hide spotlight and remove car
            setTimeout(() => {
              spotlight.style.transform = 'scale(2)';
              spotlight.style.opacity = '0';
              
              setTimeout(() => {
                if (parkingFloorRef.current) {
                  parkingFloorRef.current.removeChild(spotlight);
                  parkingFloorRef.current.removeChild(car);
                  
                  // Update ground slot status immediately
                  const groundSlot = parkingFloorRef.current.querySelectorAll('.parking-slot')[targetSlotId - 1];
                  if (groundSlot) {
                    groundSlot.style.backgroundColor = 'rgba(255, 92, 92, 0.1)';
                    groundSlot.style.border = '3px solid #ff3a3a';
                    groundSlot.style.color = '#FF5C5C';
                    groundSlot.style.boxShadow = '0 0 10px rgba(255, 58, 58, 0.3)';
                    
                    // Add car to the slot
                    const carElement = document.createElement('div');
                    carElement.className = 'parked-car';
                    carElement.style.width = '65%';
                    carElement.style.height = '60%';
                    carElement.style.position = 'absolute';
                    carElement.style.backgroundImage = `url(${targetSlotId % 2 === 0 ? '/car2.png' : '/car.png'})`;
                    carElement.style.backgroundSize = 'contain';
                    carElement.style.backgroundRepeat = 'no-repeat';
                    carElement.style.backgroundPosition = 'center';
                    carElement.style.zIndex = '2';
                    carElement.style.boxShadow = '0 3px 6px rgba(0,0,0,0.3)';
                    groundSlot.appendChild(carElement);
                  }
                  
                  // Create data particle (CCTV to phone)
                  createDataParticle(targetSlotId);
                  
                  // Update app slot status only after data particle animation completes
                  setTimeout(() => {
                    // Update app slot status in React state
                    setSlots(prevSlots => {
                      const newSlots = [...prevSlots];
                      newSlots[targetSlotId - 1] = 'occupied';
                      return newSlots;
                    });
                    
                    // Set isAnimating to false to allow next car
                    setIsAnimating(false);
                  }, 2000); // Wait for data particle animation to complete (approx 2 seconds)
                }
              }, 300);
            }, 1000);
          }, 10);
        }, 1000);
      }, 1000);
    }, 100);
  }, [createDataParticle, isAnimating]);
  
  // Function to book slot
  const bookSlot = () => {
    const availableSlotIndices = slots
      .map((status, index) => status === 'available' ? index : -1)
      .filter(index => index !== -1);
    
    if (availableSlotIndices.length > 0) {
      const randomIndex = Math.floor(Math.random() * availableSlotIndices.length);
      const selectedSlotIndex = availableSlotIndices[randomIndex];
      
      const newSlots = [...slots];
      newSlots[selectedSlotIndex] = 'selected';
      setSlots(newSlots);
      
      // Show notification
      setShowNotification(true);
      setTimeout(() => {
        setShowNotification(false);
      }, 3000);
    }
  };
  
  // Simple continuous back-and-forth movement for spotlight
  useEffect(() => {
    let animationFrameId;
    let startTime = Date.now();
    
    const animate = () => {
      const now = Date.now();
      const elapsed = now - startTime;
      
      // Simple sine wave oscillation for smooth movement
      // Period of 6 seconds for a complete cycle
      const angle = 45 * Math.sin(elapsed / 3000 * Math.PI);
      
      setSpotlightRotation(angle);
      
      animationFrameId = requestAnimationFrame(animate);
    };
    
    animationFrameId = requestAnimationFrame(animate);
    
    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, []);
  
  // Sequential car animation with proper timing
  useEffect(() => {
    // Define the slots that will remain empty
    const emptySlots = [7, 12];
    
    // Define the slots to fill in order
    const slotsToFill = [1, 2, 3, 4, 5, 6, 8, 9, 10, 11, 13, 14, 15]
      .filter(slot => !emptySlots.includes(slot));
    
    let currentIndex = 0;
    
    // Function to create the next car
    const createNextCar = () => {
      if (currentIndex >= slotsToFill.length) return;
      
      const slotId = slotsToFill[currentIndex];
      
      // Check if slot is already occupied
      if (slots[slotId - 1] !== 'occupied') {
        createMovingCar(slotId);
        currentIndex++;
      } else {
        // If slot is already occupied, move to next slot
        currentIndex++;
        createNextCar();
      }
    };
    
    // Start the first car after a short delay
    const initialTimer = setTimeout(() => {
      createNextCar();
    }, 500);
    
    // Set up an interval to check if we can create the next car
    const checkInterval = setInterval(() => {
      if (!isAnimating && currentIndex < slotsToFill.length) {
        createNextCar();
      }
    }, 7000); // Check every 7 seconds
    
    return () => {
      clearTimeout(initialTimer);
      clearInterval(checkInterval);
    };
  }, [createMovingCar, slots, isAnimating]);
  
  return (
    <AnimationContainer>
      {/* Parking Area */}
      <ParkingArea>
        <Wall>
          {/* CCTV Camera */}
          <CCTV>
            <CCTVBody className="cctv-body">
              <CCTVLens />
              <CCTVLight />
            </CCTVBody>
            <CCTVMount />
          </CCTV>
          
          {/* Curved Spotlight */}
          <SpotlightContainer $rotation={spotlightRotation}>
            <SpotlightCurve />
          </SpotlightContainer>
        </Wall>
        
        {/* Parking Floor with Slots */}
        <ParkingFloor ref={parkingFloorRef}>
          {slots.map((status, index) => (
            <ParkingSlot
              key={index}
              $status={status}
              className="parking-slot"
            >
              {index + 1}
              {status === 'occupied' && (
                <Car
                  $carImage={(index + 1) % 2 === 0 ? '/car2.png' : '/car.png'}
                />
              )}
            </ParkingSlot>
          ))}
        </ParkingFloor>
      </ParkingArea>
      
      {/* Mobile App Interface */}
      <AppInterface>
        <Phone>
          <PhoneScreen>
            <PhoneHeader>
              <h2>ParkGanga</h2>
            </PhoneHeader>
            <PhoneMap>
              <PhoneSlots ref={phoneSlotsRef}>
                {slots.map((status, index) => (
                  <PhoneSlot
                    key={index}
                    $status={status}
                    className="phone-slot"
                  >
                    {index + 1}
                  </PhoneSlot>
                ))}
              </PhoneSlots>
            </PhoneMap>
            <PhoneButton onClick={bookSlot}>Book Parking Slot</PhoneButton>
            <PhoneTabs>
              <PhoneTab $active={true}>Map</PhoneTab>
              <PhoneTab>Bookings</PhoneTab>
              <PhoneTab>Profile</PhoneTab>
            </PhoneTabs>
          </PhoneScreen>
        </Phone>
      </AppInterface>
      
      {/* Notification */}
      <Notification $show={showNotification} ref={notificationRef}>
        Parking slot booked successfully!
      </Notification>
    </AnimationContainer>
  );
};

export default SmartParkingAnimation;
